import Lenis from 'lenis';
import { EventEmitter } from '@/core/EventEmitter';
import { settings } from '@/config';
import type { ScrollDirection, ScrollState, TickState } from '@/types';

/**
 * Lenis integration. Driven by the single rAF loop (one clock for the app) and
 * exposes normalized scroll values — progress / velocity / direction — that will
 * drive future scroll animations. Phase 01 creates NO sections; the scrollable
 * height comes from the transparent spacer in index.html.
 */
export class ScrollManager extends EventEmitter<{ scroll: ScrollState }> {
  private readonly lenis: Lenis;

  readonly state: ScrollState = {
    scroll: 0,
    limit: 0,
    progress: 0,
    velocity: 0,
    direction: 0,
  };

  /** Optional gate on the end-of-scroll loop snap (2026-09-01): raw scroll can now reach the
   *  end seconds before the speed-capped CAMERA does (magnet triggers fire off the camera), and
   *  snapping raw scroll to 0 mid-chase would yank the camera backward through every scene.
   *  `Experience` wires this to "the camera is also at the end". `null` = ungated. */
  loopGate: (() => boolean) | null = null;

  constructor() {
    super();
    document.documentElement.style.setProperty('--scroll-pages', String(settings.scroll.pages));

    // TOUCH DEVICES DRIVE LENIS DIRECTLY (2026-09-04, user: "on phone nothing works on
    // scroll — the camera is not animating"). With syncTouch off, touch relies on NATIVE
    // window scroll propagating up past the fixed full-viewport canvas — which is exactly the
    // kind of propagation mobile browsers get wrong. syncTouch makes Lenis consume touchmove
    // itself, so the same virtual-scroll path drives phone and desktop alike — and the
    // journey's scroll LOCKS (lenis.stop) finally hold on touch too.
    const coarse =
      typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
    this.lenis = new Lenis({
      lerp: settings.scroll.lerp,
      smoothWheel: settings.scroll.smoothWheel,
      wheelMultiplier: settings.scroll.wheelMultiplier,
      touchMultiplier: settings.scroll.touchMultiplier,
      syncTouch: settings.scroll.syncTouch || coarse,
    });
  }

  /** Advance Lenis with the shared clock, then publish a fresh snapshot. */
  update(tick: TickState): void {
    this.lenis.raf(tick.elapsedMs);

    this.state.scroll = this.lenis.scroll;
    this.state.limit = this.lenis.limit;
    this.state.progress = Number.isFinite(this.lenis.progress) ? this.lenis.progress : 0;
    this.state.velocity = this.lenis.velocity;
    this.state.direction = (this.lenis.direction ?? 0) as ScrollDirection;

    // Loop the experience: once scroll reaches the very end, snap back to the top so
    // continuing to scroll re-plays the baked camera path from the start instead of
    // just sticking on the last frame.
    if (settings.scroll.loop && this.state.progress >= 1 && (this.loopGate?.() ?? true)) {
      this.lenis.scrollTo(0, { immediate: true });
    }

    this.emit('scroll', this.state);
  }

  resize(): void {
    this.lenis.resize();
  }

  /** Live "camera movement speed": how much the baked camera advances per unit of scroll
   *  input. Lenis reads `options.wheelMultiplier`/`touchMultiplier` on every wheel/touch
   *  event, so writing them takes effect immediately (debug GUI). */
  setSpeed(multiplier: number): void {
    settings.scroll.wheelMultiplier = multiplier;
    settings.scroll.touchMultiplier = multiplier * 1.2;
    const opts = this.lenis.options as { wheelMultiplier?: number; touchMultiplier?: number };
    opts.wheelMultiplier = multiplier;
    opts.touchMultiplier = multiplier * 1.2;
  }

  /** Stop accepting new scroll input (wheel/touch) — used while a scene transition
   *  plays, so the camera can glide/decelerate into its new target undisturbed
   *  instead of the user being able to yank it further mid-transition. */
  lockInput(): void {
    this.lenis.stop();
  }

  /** Resume accepting scroll input after a transition (+ its settle delay) finishes. */
  unlockInput(): void {
    this.lenis.start();
  }

  /** Future API for scene transitions / section triggers. */
  scrollTo(
    target: Parameters<Lenis['scrollTo']>[0],
    options?: Parameters<Lenis['scrollTo']>[1],
  ): void {
    this.lenis.scrollTo(target, options);
  }

  dispose(): void {
    this.lenis.destroy();
    this.clearListeners();
  }
}
